import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { GoogleMap, InfoWindowF, MarkerF } from '@react-google-maps/api';
import { NextImage, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from '../../lib/component-props';
import useZipcodeSearch from '../../lib/hooks/coveo/useZipcodeSearch';
import zipcodeSearchEngine from '../../lib/utils/coveo/zipcodeSearchEngine';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedStudy } from '../../../src/features/crStudyDetailsSlice';
import { hideLoader, showLoader } from '../../../src/features/loaderSlice';
import { RootState } from '../../../src/store/store';
import IconMarker from '../../../public/icon-marker.svg';
import StudyLocationCard from './StudyLocationCard';
import { ZipCodeWrapper, ZipCodeTextField } from './TrialDetails.styles';
import useGeolocation from '../../lib/hooks/useGeoLocation';
import ButtonPrimary from '../shared/Button/PrimaryButton';
import UiBox from '../shared/Box';
import UiHeading from '../shared/Heading';
import getTooltipContentFirstLine from './getStudyLocationTooltipContent';
import { formatContentWithMergetag, studyLocationMergeFieldMap } from './StudyLocationMergeTag';

type StudyLocationMapProps = ComponentProps & any;

const StudyLocationMap = (props: StudyLocationMapProps): JSX.Element => {
  const theme = useTheme();
  const {
    control,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: 'all' });
  const { sitecoreContext } = useSitecoreContext();
  //const siteSettings: any = sitecoreContext?.siteSettings;
  const userProfile = useSelector((state: RootState) => state.crProfile);
  const selectedStudy = useSelector((state: RootState) => state.crStudyDetails.selectedStudy);
  const profileDetails = userProfile?.profileData?.basicDetails;
  const coveoResult = useSelector((state: RootState) => state.coveoResult);
  const [zipcode, setZipcode] = useState<string>(coveoResult?.zipCode || profileDetails?.zipCode);
  const [trialsList, setTrialsList] = useState<any>([]);
  const [mapLoaded, setMapLoaded] = useState<boolean>(false);
  const { location, isLoaded: isMapsScriptLoaded } = useGeolocation();
  const route: any = sitecoreContext?.route;
  const { state } = useZipcodeSearch(
    zipcodeSearchEngine,
    zipcode,
    route?.fields?.orgStudyId?.value,
    props?.fields?.siteDistanceRange?.value || 50 //distance harcoded need to get from sitecore
  );
  const cardTitleFormat = props?.fields?.siteNameFormat;
  const mapPinTooltipFormat = props?.fields?.tooltipFormat?.value;

  const dispatch = useDispatch();
  // Use isLoaded from useGeolocation to avoid loading the Google Maps script twice
  // (useGeolocation already loads it via useLoadScript; loading again causes "included multiple times" error)
  const isLoaded = isMapsScriptLoaded;

  const containerStyle = {
    width: '100%',
    height: '500px',
  };

  const handleMapLoad = () => {
    setMapLoaded(true);
  };

  const onClickMarker = (trials: any) => {
    if (selectedStudy) {
      dispatch(setSelectedStudy(trials));
    }
  };

  const getDistinctLocations = (locationResults: any) => {
    try {
      if (!locationResults) return [];
      const getKey = (obj: {
        raw: {
          locationcity: any;
          locationcountry: any;
          locationsitename: any;
          locationzip: any;
          triallatitude: any;
          triallongitude: any;
        };
      }) =>
        `${obj?.raw?.locationcity || ''}-${obj?.raw?.locationcountry || ''}-${
          obj?.raw?.locationsitename || ''
        }-${obj?.raw?.locationzip || ''}-${obj?.raw?.triallatitude || ''}-${
          obj?.raw?.triallongitude || ''
        }}`;

      const map = new Map();
      locationResults?.forEach(
        (obj: {
          raw: {
            locationcity: any;
            locationcountry: any;
            locationsitename: any;
            locationzip: any;
            triallatitude: any;
            triallongitude: any;
          };
        }) => {
          const key = getKey(obj);
          if (!map.has(key)) {
            map.set(key, obj);
          }
        }
      );

      const distinctLocationResults = Array.from(map.values());
      return distinctLocationResults;
    } catch (error) {
      return [];
    }
  };

  const clickedInfoWindow = (lat: number, lng: number) => {
    return (
      <InfoWindowF position={{ lat: lat, lng: lng }}>
        <StudyLocationCard
          titleFormat={cardTitleFormat}
          enrollingText={props?.fields?.enrollingText}
          notYetEnrollingText={props?.fields?.notYetEnrollingText}
          siteAddressFormat={props?.fields?.siteAddressFormat}
          studyDetails={selectedStudy}
        />
      </InfoWindowF>
    );
  };

  const renderMap = useCallback(() => {
    const extractedTooltipContent = getTooltipContentFirstLine(mapPinTooltipFormat);

    return (
      <>
        {mapLoaded &&
          trialsList &&
          trialsList.length > 0 &&
          trialsList?.map((trials: any, trialsIndex: number) => {
            const mapper = studyLocationMergeFieldMap(trials?.raw);
            const tooltipContent = formatContentWithMergetag(extractedTooltipContent, mapper);
            return (
              <MarkerF
                key={trialsIndex}
                position={{
                  lat: trials?.raw?.triallatitude,
                  lng: trials?.raw?.triallongitude,
                }}
                title={tooltipContent}
                icon={
                  trialsIndex === 0 ? (
                    <IconMarker></IconMarker>
                  ) : (
                    props?.fields?.sectionIcon?.value?.src
                  )
                }
                onClick={() => onClickMarker(trials)}
              >
                {selectedStudy &&
                selectedStudy?.raw?.locationsitecoreid === trials?.raw?.locationsitecoreid
                  ? clickedInfoWindow(
                      selectedStudy?.raw?.triallatitude,
                      selectedStudy?.raw?.triallongitude
                    )
                  : null}
              </MarkerF>
            );
          })}
      </>
    );
  }, [trialsList, selectedStudy]);

  const handleZipcodeSearch = handleSubmit(async () => {
    dispatch(showLoader());
    const zipcode = getValues('zipcode');
    setZipcode(zipcode);
    dispatch(hideLoader());
  });

  useEffect(() => {
    const distinctLocations = getDistinctLocations(state?.results);
    setTrialsList(distinctLocations);
    dispatch(setSelectedStudy(distinctLocations && distinctLocations[0]));
  }, [state]);

  return (
    <>
      <style jsx global>{`
        /* Hide StudyLocationMap on initial load in iframe mode to prevent flicker */
        html.iframe-mode .study-location-map {
          display: none !important;
        }
      `}</style>
      <Box
        className="study-location-map"
        sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <NextImage field={props?.fields?.sectionIcon} />
          <UiHeading size="h3" data-testid="title" isChildren color="primary">
            <Text field={props?.fields?.sectionTitle} />
          </UiHeading>
        </Box>
        <Box>
          <Typography sx={{ color: theme.palette.neutral8.main }}>
            <Text field={props?.fields?.searchHeading} />
          </Typography>
          <ZipCodeWrapper>
            <Controller
              defaultValue={zipcode}
              rules={{
                required: {
                  value: props?.fields?.isRequired?.value,
                  message: 'Zipcode is required',
                },
                pattern: {
                  value: new RegExp(props?.fields?.zipCodeRegex?.fields?.value?.value),
                  message: 'Invalid Zipcode',
                },
              }}
              control={control}
              name="zipcode"
              render={({ field }) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <ZipCodeTextField
                    sx={{ width: '100%' }}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    error={Boolean(errors?.zipcode)} // Set the error prop based on the presence of an error
                    {...field}
                  />
                  {errors.zipcode && (
                    <Stack style={{ marginTop: '0px' }} alignItems="center" direction="row">
                      <Typography color="error">{`${errors?.zipcode?.message}`}</Typography>
                    </Stack>
                  )}
                </Box>
              )}
            />
            <UiBox>
              <ButtonPrimary width="205px" onClick={handleZipcodeSearch}>
                <Text field={props?.fields?.searchButtonText} />
              </ButtonPrimary>
            </UiBox>
          </ZipCodeWrapper>
        </Box>
        <Box
          className="cr-nearest-trials-map"
          sx={{
            borderBottomRightRadius: '0.5rem',
            borderTopRightRadius: '0.5rem',
            '.gm-style-iw-c': {
              padding: '0px',
              overflow: 'visible',
            },
            '.gm-style-iw-d': {
              overflow: 'visible !important',
            },
            '.gm-style-iw-d + button': {
              display: 'none !important',
            },
            '.gm-style-iw-chr': {
              display: 'none !important',
            },
            height: '100%',
            width: '100%',
          }}
        >
          {isLoaded && (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={{
                lat: selectedStudy
                  ? selectedStudy?.raw?.triallatitude
                  : location
                    ? location?.latitude
                    : 49.5604016,
                lng: selectedStudy
                  ? selectedStudy?.raw?.triallongitude
                  : location
                    ? location?.longitude
                    : -109.1136617,
              }}
              zoom={3}
              onLoad={handleMapLoad}
              options={{ scrollwheel: false, mapTypeControl: false }}
            >
              {renderMap()}
            </GoogleMap>
          )}
        </Box>
      </Box>
    </>
  );
};

export default StudyLocationMap;
